import { map, mapKeys, indexOf } from 'lodash';
import Decimal from 'decimal.js';
import ReactGA from 'react-ga';
import remoteEvent from '@tools/remoteEvent';

export const dropZero = (str) => {
  if (!str) return '-';
  const e = new Decimal(str);
  return e.toFixed();
};

export const getOtcVisible = (list, currentLang, currency) => {
  // 根据后台返回的语言列表限制是否显示otc 从外部传入
  const langList = map(list, (item) => item.typeCode) || [];
  if (langList.indexOf(currentLang) > -1) {
    if (currentLang === 'en_US' && ['IDR', 'VND', 'CNY'].indexOf(currency || '') < 0) {
      return false;
    }
    return true;
  }
  return false;
};

/**
 * 增加千分位分隔符
 * @param n
 * @returns {string}
 */
const SeparateNumberPool = {
  pool: Object.create(null),
  poolCount: 0,
  has(k) {
    return !!this.pool[k];
  },
  get(k) {
    return this.pool[k];
  },
  set(k, v) {
    if (this.poolCount > 100000) {
      // 清理缓存
      this.poolCount = 0;
      this.pool = Object.create(null);
    }
    if (!this.has(k)) {
      this.poolCount += 1;
    }
    this.pool[k] = v;
  },
};
export const separateNumber = (n) => {
  if (typeof +n !== 'number') {
    return n;
  }
  const num = `${n}`;

  if (SeparateNumberPool.has(num)) {
    return SeparateNumberPool.get(num);
  }
  if (!/^[0-9.]+$/.test(num)) {
    return n;
  }

  let integer = num;
  let floater = '';
  if (num.indexOf('.') > -1) {
    const arr = num.split('.');
    [integer, floater] = arr;
  }
  const len = integer.length;
  let parser = '';
  if (len > 3) {
    let count = 0;
    for (let i = len - 1; i >= 0; i -= 1) {
      parser = integer[i] + parser;
      count += 1;
      if (count % 3 === 0 && i > 0) {
        parser = `,${parser}`;
      }
    }
  } else {
    parser = integer;
  }
  if (floater !== '') {
    floater = `.${floater}`;
  }
  const r = `${parser}${floater}`;
  SeparateNumberPool.set(num, r);

  return r;
};

export const formatNumber = (num, precision) => {
  if (typeof +num !== 'number' || Number.isNaN(+num)) {
    return num;
  }
  return separateNumber(dropZero(numberFixed(num, precision)));
};

/**
 * @description 高精度乘法并取给定位数向下取整
 * @param a
 * @param b
 * @param decimal
 * @param round
 * @returns {string|*}
 */
export const multiplyFloor = (a, b, decimal = 8, round = Decimal.ROUND_FLOOR) => {
  return new Decimal(a).mul(b).toFixed(decimal, round);
};

/**
 * 高精度指定位数
 * @param v
 * @param decimal
 * @param round
 * @returns {*}
 */
export const numberFixed = (v, decimal, round = Decimal.ROUND_DOWN) => {
  const numberV = +v;
  if (typeof numberV !== 'number' || v === undefined) {
    return v;
  }
  if (numberV === 0) {
    return '0';
  }
  const stringV = v.toString(); // 防止数值超过最大范围，导致转换不准确
  return new Decimal(stringV).toFixed(decimal, round);
};

/**
 * 合并组件的classes和传入的classes
 * @param {object} classes 组件本身定义的classes
 * @param {object} classesFromProps 传入的classes，组件使用者定义的类名集
 * @returns {string}
 */
export const mergeClasses = (classes, classesFromProps = {}) => {
  let result;
  const _classesFromProps = {};
  try {
    mapKeys(classesFromProps, (value, key) => {
      if (classes[key]) {
        _classesFromProps[key] = `${classes[key]} ${classesFromProps[key]}`;
      }
    });
    result = { ...classes, ..._classesFromProps };
  } catch (e) {
    result = classes || {};
  }
  return result;
};

/**
 * 转换成百分比
 * @param {*} num
 */
export const floadToPercent = (num) => {
  // console.log('floadToPercent', num);
  if (typeof +num !== 'number' || Number.isNaN(+num)) {
    return num;
  }
  return `${new Decimal(+num).mul(100).toFixed()}%`;
};

export const resolveFee = (num, isDiscount, discountRate = 80) => {
  if (+num > 0) {
    return floadToPercent(
      isDiscount ? new Decimal(num).mul(new Decimal(discountRate || 100).div(100)) : num,
    );
  }
  return floadToPercent(num);
};

/**
 * 判断是否子账号
 */
export const checkIsSub = (obj = {}) => {
  const { isSub = false, type = 1 } = obj || {};
  return isSub || type === 3;
};

export const getScrollY = (parent = () => window) => {
  if (parent.pageYOffset !== undefined) {
    return parent.pageYOffset;
  }
  if (parent.scrollTop !== undefined) {
    return parent.scrollTop;
  }
  return (document.documentElement || document.body.parentNode || document.body).scrollTop;
};

/**
 * 合并埋点动作
 * eventLevelVariables: 事件级别变量 eg: {ad_pop_id: '1', ad_pop_module: 'assets'}
 */
export const ga = async (key, eventLevelVariables) => {
  // console.log('--埋点--', key, eventLevelVariables);
  if (!key) return;
  if (window._hmt && typeof window._hmt.push === 'function') {
    window._hmt.push(['_trackEvent', key, 'click']);
  }
  if (typeof window.gio === 'function') {
    window.gio('track', key, eventLevelVariables);
  }
  // eslint-disable-next-line no-unused-expressions
  remoteEvent.emit(remoteEvent.evts.GET_REPORT, (Report) => {
    Report.logAction(key, 'click');
  });
  ReactGA.event({ category: key, action: 'click' }); // react-ga
};

/**
 * 获取携带相关属性的node节点
 *
 * @param {HTMLNode} node node节点
 * @param {string} attr 查询的属性
 */
export const getGaElement = (node, attr) => {
  if (!node || node.localName === 'body') {
    return null;
  }
  const key = node.getAttribute(attr);
  if (key) {
    return node;
  }

  if (!node.parentNode) {
    return null;
  }

  return getGaElement(node.parentNode, attr);
};

/**
 * 新版本自定义点击埋点
 */
export const gaClickNew = async (key, obj) => {
  if (!key) return;
  const { siteid, pageid, modid, eleid, ...other } = obj || {};
  ga(key, {
    ...other,
    str1: siteid,
    str2: `${siteid}.${pageid}`,
    str3: `${siteid}.${pageid}.${modid}`,
    str4: `${siteid}.${pageid}.${modid}.${eleid}`,
  });
};

export const getMenuSpm = (index = 0, subIndex = 0) => {
  return [`Header${index + 1}`, String(subIndex + 1)];
};

export const compose = (spm = []) => {
  let spmId = '';
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    if (spm) {
      const siteId = sensors.spm.getSiteId();
      const pageId = sensors.spm.getPageId();
      if (siteId && pageId) {
        spmId = sensors.spm.compose(spm);
      }
    }
  });
  return spmId;
};

export const kcsensorsManualTrack = (menuSpm, params = {}, type = 'expose') => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    if (!menuSpm) return;
    // kcsensors spm 模块
    const exposeCfg = {
      spm_id: sensors.spm.compose(menuSpm),
      ...params,
      pagecate: 'topNavigation',
    };
    sensors.track(type, exposeCfg);
  });
};

export const kcsensorsMenuClick = (menuSpm, params = {}) => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    if (!menuSpm) return;
    sensors.trackClick(menuSpm, { ...params, pagecate: 'topNavigation' });
  });
};

export const kcsensorsClick = (spm = [], data = {}) => {
  remoteEvent.emit(remoteEvent.evts.GET_SENSORS, (sensors) => {
    if (!spm) return;
    sensors.trackClick(spm, { ...data, pagecate: 'topNavigation' });
  });
};

// 向url添加参数
const updateQueryStringParameter = (uri, key, value) => {
  if (!uri || !value) {
    return uri;
  }
  const reg = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
  const separator = indexOf(uri, '?') > -1 ? '&' : '?';
  if (uri.match(reg)) {
    return uri.replace(reg, `$1${key}=${value}$2`);
  }

  return `${uri}${separator}${key}=${value}`;
};

export const addLangToPath = (_href, lang) => {
  if (lang && _href && (_href.match(/^http?/) || _href.startsWith('/'))) {
    let base = '';
    if (lang === 'zh_HK') {
      base = 'zh-hant';
    } else if (lang === 'zh_CN') {
      base = 'zh-hans';
    } else {
      [base] = lang.split('_');
    }
    const { origin } = window.location;
    const _url = new URL(_href.match(/^https?/) ? _href : `${origin}${_href}`);
    // 不需要语言子路径的路由
    const withOutLangPath = window.WITHOOU_LANG_PATH || [];
    if (withOutLangPath && withOutLangPath.length) {
      const withOutLang = withOutLangPath.some((item) => {
        return _url.pathname === item;
      });
      if (withOutLang) {
        return _href;
      }
    }
    // 语言子路径项目的域名
    const hasLangPathDomain = window.LANG_DOMAIN || [
      'www.kucoin.',
      'trade.kucoin.',
      'futures.kucoin.',
      'm.kucoin.',
    ];
    const accord = hasLangPathDomain.some((item) => _url.hostname.startsWith(item));
    if (base && accord) {
      const homePage = _url.pathname && _url.pathname === `/${base}`;
      if (base !== 'en' && !(_url.pathname && _url.pathname.startsWith(`/${base}/`)) && !homePage) {
        if (_url.pathname === '/') {
          _url.pathname = `/${base}`;
        } else {
          _url.pathname = `/${base}${_url.pathname}`;
        }
      }
      const urlWithPath = removeLangQuery(_url.toString());
      return urlWithPath;
    }
    return _href;
  }
  return _href;
};

export const removeLangQuery = (url = '') => {
  if (!url) {
    return url;
  }
  const langIndex = url.indexOf('lang=');
  if (langIndex !== -1) {
    const query = url.substr(url.indexOf('?') + 1);
    const queryArr = query.split('&') || [];
    if (queryArr && queryArr.length === 1) {
      if (queryArr[0].indexOf('lang=') === 0) {
        return url.substr(0, url.indexOf('?'));
      }
      return url;
    }
    let langQueryLen = 0;
    queryArr.forEach((item) => {
      if (item?.indexOf('lang=') === 0) {
        langQueryLen = item.length;
      }
    });
    // 有多个参数
    if (url[langIndex - 1] === '?') {
      return `${url.substr(0, url.indexOf('?') + 1)}${url.slice(url.indexOf('&') + 1)}`;
    }
    const delIndex = url.indexOf('&lang=');
    return `${url.substr(0, delIndex)}${url.substr(delIndex + langQueryLen + 1, url.length)}`;
  }
  return url;
};

// 向query参数中添加spm
export const addSpmIntoQuery = (url, spms, currentLang) => {
  const spm = compose(spms);
  const href = currentLang ? addLangToPath(url, currentLang) : url; // 增加语言子路径
  if (!spm) return href;
  return updateQueryStringParameter(href, 'spm', spm);
};

/**
 * SSG 环境不渲染
 *
 * @param fallback  定义当在ssg 环境运行时进行的渲染;
 */
export const NoSSG = ({ children, fallback = null }) => {
  const isSSG = navigator.userAgent.indexOf('SSG_ENV') > -1;

  if (isSSG) {
    return fallback;
  }
  return children;
};
